import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

export const MIN_CLIENT_SEARCH_LENGTH = 3;

/** Garante lista de clientes mesmo quando a API/fetchData devolve envelope paginado. */
const normalizeClientsList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.clients)) return raw.clients;
  }
  return [];
};

/** Busca clientes por estágio (função assíncrona, não é hook). */
export const getClientsByStage = async (stageId, page = 1, limit = 50) => {
  const response = await fetchData(
    `/clients?stageId=${stageId}&page=${page}&limit=${limit}`,
    "GET"
  );
  const list = response?.data ?? response ?? [];
  return Array.isArray(list) ? list : [];
};

/**
 * @param {number} [page=1]
 * @param {number} [limit=10]
 * @param {string} [search=""]
 * @param {number|number[]|string|null} [idsProjetos] - quando informado, filtra clientes pela(s) empresa(s); na URL para não ser sobrescrito pelo interceptor
 * @param {string|object} [filtersOrOptions=""] - query string de filtros avançados OU options do react-query (retrocompat)
 * @param {object} [optionsArg={}] - options do react-query quando o 5º arg é filtersQuery
 */
export const useClients = (page = 1, limit = 10, search = "", idsProjetos = null, filtersOrOptions = "", optionsArg = {}) => {
  const queryClient = useQueryClient();

  /** Retrocompat: 5º arg era `options` do react-query antes de filtros avançados. */
  let filtersQuery = "";
  let options = {};
  if (typeof filtersOrOptions === "object" && filtersOrOptions !== null && !Array.isArray(filtersOrOptions)) {
    options = filtersOrOptions;
  } else {
    filtersQuery = typeof filtersOrOptions === "string" ? filtersOrOptions : "";
    options =
      typeof optionsArg === "object" && optionsArg !== null && !Array.isArray(optionsArg) ? optionsArg : {};
  }

  /** Alinhado a MIN_SEARCH_LENGTH no backend: com 1-2 caracteres o prefixo casa quase tudo. */
  const effectiveSearch =
    typeof search === "string" && search.trim().length >= MIN_CLIENT_SEARCH_LENGTH ? search : "";

  const idsProjetosParam = (() => {
    if (idsProjetos == null) return "";
    if (Array.isArray(idsProjetos)) return idsProjetos.filter((n) => Number.isFinite(Number(n))).join(",");
    if (typeof idsProjetos === "number" && Number.isFinite(idsProjetos)) return String(idsProjetos);
    const s = String(idsProjetos).trim();
    return s ? s : "";
  })();

  const filtersPart = filtersQuery ? `&${filtersQuery.replace(/^\?/, "")}` : "";

  const url =
    `/clients?page=${page}&limit=${limit}&search=${encodeURIComponent(effectiveSearch)}` +
    (idsProjetosParam ? `&ids_projetos=${encodeURIComponent(idsProjetosParam)}` : "") +
    filtersPart;

  const query = useQuery(
    ["clients", page, limit, search, idsProjetosParam, filtersQuery],
    () => fetchData(url),
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      select: (response) => {
        const raw = response?.data;
        const nested =
          raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
        return {
          clients: normalizeClientsList(raw),
          pagination:
            response?.pagination ?? nested?.pagination ?? { total: 0 },
        };
      },
      ...(options || {}),
    }
  );

  const updateMutation = useMutation(
    (client) => {
      const method = client.id ? "PUT" : "POST";
      const endpoint = client.id ? `/clients/${client.id}` : `/clients`;
      return fetchData(endpoint, method, client);
    },
    {
      onSuccess: () => queryClient.invalidateQueries("clients"),
    }
  );

  const deleteMutation = useMutation(
    (id) => fetchData(`/clients/${id}`, "DELETE"),
    {
      onSuccess: () => queryClient.invalidateQueries("clients"),
    }
  );

  return { ...query, updateMutation, deleteMutation };
};
